/**
 * ArenaX dual-theme hook + toggle button (Phase 117).
 *
 * Resolution order on first paint:
 *   1. localStorage('arenax_theme')   ← per-device preference
 *   2. window.matchMedia('(prefers-color-scheme: dark)') ← OS hint
 *   3. 'dark'                          ← cinematic default
 *
 * The resolved value is written to `<html data-theme="…">` and ALSO to
 * `<body class="arenax-body">` so the ArenaX gradient + text-color rules
 * in `arenax-theme.css` take over for the duration of an ArenaX surface.
 *
 * The toggle button (lucide Moon / Sun, 300ms cross-fade) goes in
 * `ArenaxLayout.jsx`'s navbar top-right slot.
 */
import { useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'arenax_theme';

function resolveInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (_) { /* localStorage may be blocked */ }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

/** Mount-time hook: writes <html data-theme> and adds .arenax-body to <body>
 * for the lifetime of the component (typically `ArenaxLayout`).  Returns
 * `[theme, toggleTheme]`. */
export function useArenaxTheme() {
  const [theme, setTheme] = useState(resolveInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.setAttribute('data-theme', theme);
    body.classList.add('arenax-body');
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (_) { /* ignore */ }
    return () => {
      // Leave data-theme on root so the user's choice survives navigation,
      // but drop the arenax-body class so non-ArenaX pages keep their
      // original light app palette.
      body.classList.remove('arenax-body');
    };
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return [theme, toggleTheme, setTheme];
}

/** Sun/Moon pill button.  Goes in the ArenaX top navbar (right edge). */
export function ArenaxThemeToggle({ theme, onToggle, className = '' }) {
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <Button
      onClick={onToggle}
      variant="ghost"
      size="icon"
      className={`relative h-9 w-9 rounded-full border ${className}`}
      style={{
        background: 'var(--ax-card-bg)',
        borderColor: 'var(--ax-border-quiet)',
        color: `hsl(var(--ax-text))`,
      }}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      data-testid="arenax-theme-toggle"
    >
      {theme === 'dark'
        ? <Sun className="h-4 w-4" style={{ color: `hsl(var(--ax-accent-amber))` }} />
        : <Moon className="h-4 w-4" style={{ color: `hsl(var(--ax-accent-cyan))` }} />}
    </Button>
  );
}

/** Read-only theme value for non-Layout components that need to branch on
 * theme without driving the toggle. */
export function useArenaxThemeValue() {
  const [theme, setTheme] = useState(resolveInitialTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const t = document.documentElement.getAttribute('data-theme');
      if (t === 'dark' || t === 'light') setTheme(t);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}
